import React, { useMemo } from 'react';
import { TextField } from '@mui/material';
import { Line } from '@react-three/drei';
import { DoubleSide, Shape } from 'three';

import { Line3D, LineSegment3D, Point3D, Polygon3D } from '@paragon/geometry-library';
import { getMatrixFromIsometry } from '@paragon/three-shared';

import { LayoutCoordinateVisuals } from '../../library/layout-geometry/LayoutCoordinateVisuals';
import {
  setDebugLayoutVisualsJson,
  useDebugLayoutVisuals,
  useShowDebugLayoutVisuals,
} from '../../store/layoutDebugVisualStore';

function DebugPointVisual({ point }: { point: Point3D }) {
  return (
    <mesh position={[point.x, point.y, point.z]}>
      <meshBasicMaterial color={0xff2222} />
      <sphereGeometry args={[1 / 16]} />
    </mesh>
  );
}

function LineSegment({ segment, color }: { segment: LineSegment3D; color: number }) {
  return <Line points={[segment.basePoint.toArray(), segment.endPoint.toArray()]} color={color} lineWidth={2} />;
}

function DebugLineSegmentVisual({ segment }: { segment: LineSegment3D }) {
  return <LineSegment segment={segment} color={0x889922} />;
}

function DebugLineVisual({ line }: { line: Line3D }) {
  const farPoint = line.getPointAlongLine(1200);
  const reverseFarPoint = line.getPointAlongLine(-1200);
  const segment = new LineSegment3D(farPoint, reverseFarPoint);
  return <LineSegment segment={segment} color={0x007722} />;
}

function LineSegmentsVisual({ segments, color }: { segments: readonly LineSegment3D[]; color: number }) {
  return segments.map((segment) => (
    <LineSegment
      key={`${segment.basePoint.x},${segment.basePoint.y},${segment.endPoint.x},${segment.endPoint.y}`}
      segment={segment}
      color={color}
    />
  ));
}

function DebugLineSegmentsVisual({ segments }: { segments: readonly LineSegment3D[] }) {
  return <LineSegmentsVisual segments={segments} color={0xbb6622} />;
}

function getShapeAndMatrixForPolygon(geometry: Polygon3D) {
  const shape = new Shape().moveTo(
    geometry.mappedTo2D.vertices[geometry.mappedTo2D.vertices.length - 1].x,
    geometry.mappedTo2D.vertices[geometry.mappedTo2D.vertices.length - 1].y,
  );

  for (const vertex of geometry.mappedTo2D.vertices) {
    shape.lineTo(vertex.x, vertex.y);
  }

  const matrix = getMatrixFromIsometry(geometry.embedding.toIsometry());

  return { shape, matrix };
}

function DebugPolygonVisual(props: { geometry: Polygon3D }) {
  const { shape: planeShape, matrix: planeMatrix } = useMemo(
    () => getShapeAndMatrixForPolygon(props.geometry),
    [props.geometry],
  );

  return (
    <>
      <mesh matrix={planeMatrix} matrixAutoUpdate={false} renderOrder={-1000}>
        <shapeGeometry args={[planeShape]} />
        <meshBasicMaterial transparent side={DoubleSide} color={0x22ff22} opacity={0.3} />
      </mesh>
      <LineSegmentsVisual segments={props.geometry.edges} color={0x22ff22} />
    </>
  );
}

export function LayoutDebugVisuals() {
  const showDebugVisuals = useShowDebugLayoutVisuals();
  const objects = useDebugLayoutVisuals();
  return showDebugVisuals ? (
    <LayoutCoordinateVisuals>
      {objects.map((object, i) =>
        object instanceof Polygon3D ? (
          <DebugPolygonVisual key={i} geometry={object} />
        ) : object instanceof LineSegment3D ? (
          <DebugLineSegmentVisual key={i} segment={object} />
        ) : object instanceof Point3D ? (
          <DebugPointVisual key={i} point={object} />
        ) : object instanceof Line3D ? (
          <DebugLineVisual key={i} line={object} />
        ) : (
          <DebugLineSegmentsVisual key={i} segments={object} />
        ),
      )}
    </LayoutCoordinateVisuals>
  ) : null;
}

export const LayoutDebugVisualsTextInput = () =>
  useShowDebugLayoutVisuals() ? (
    <TextField
      id="outlined-multiline-flexible"
      label="Debug Visuals"
      multiline
      maxRows={10}
      onChange={(e) => setDebugLayoutVisualsJson(e.target.value)}
    />
  ) : null;
